import { z } from 'zod';
import { cursorPageSchema, isoDateTimeSchema, uuidSchema } from './common.js';
/**
 * The AI chat (docs/ai-chat.md): a Claude agent in the `ai-chat` container that reaches the
 * kitchen through tools – the pantry, the recipe library, the source search, "recipe from a
 * video", the URL import and the shopping list. What the model says arrives as blocks: text,
 * tool lines with a card, questions before a change, and compaction markers.
 */
export const aiChatCardSchema = z.object({
    /** What the card opens: a recipe, an AI draft under review, an import job, a shopping list. */
    kind: z.enum(['recipe', 'generation', 'import', 'shoppingList']),
    id: uuidSchema,
    title: z.string(),
    subtitle: z.string().nullable().default(null),
});
/** Tool lines and questions carry a key and parameters; the client chooses the words. */
export const aiChatToolParamsSchema = z.record(z.union([z.string(), z.number(), z.boolean()]));
export const aiChatBlockSchema = z.discriminatedUnion('type', [
    z.object({ type: z.literal('text'), text: z.string(), open: z.boolean().optional() }),
    z.object({
        type: z.literal('tool'),
        name: z.string(),
        phase: z.enum(['start', 'done', 'error']),
        params: aiChatToolParamsSchema.default({}),
        /** A readable error from the tool (phase "error"); shown as it is. */
        message: z.string().nullable().optional(),
        card: aiChatCardSchema.nullable().optional(),
    }),
    z.object({
        type: z.literal('ask'),
        askId: z.string(),
        tool: z.string(),
        params: aiChatToolParamsSchema.default({}),
        answer: z.boolean().nullable(),
    }),
    z.object({ type: z.literal('compacted'), trigger: z.enum(['manual', 'auto']) }),
]);
export const aiChatMessageStatusSchema = z.enum(['done', 'running', 'error', 'interrupted']);
export const aiChatMessageSchema = z.object({
    id: uuidSchema,
    role: z.enum(['user', 'assistant', 'system']),
    content: z.array(aiChatBlockSchema),
    status: aiChatMessageStatusSchema,
    error: z.string().nullable(),
    createdAt: isoDateTimeSchema,
});
export const aiChatConversationSchema = z.object({
    id: uuidSchema,
    title: z.string(),
    status: z.enum(['idle', 'running']),
    /** How full the model's context is after the last turn (0–100), null before the first. */
    contextPercent: z.number().int().nullable(),
    tokensTotal: z.number().int(),
    hasSession: z.boolean(),
    createdAt: isoDateTimeSchema,
    updatedAt: isoDateTimeSchema,
});
export const aiChatConversationSummarySchema = aiChatConversationSchema.extend({
    preview: z.string().nullable(),
});
export const aiChatConversationPageSchema = cursorPageSchema(aiChatConversationSummarySchema);
export const aiChatConversationDetailSchema = z.object({
    conversation: aiChatConversationSchema,
    messages: z.array(aiChatMessageSchema),
});
export const aiChatListQuerySchema = z.object({
    cursor: z.string().min(1).max(512).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(20),
    q: z.string().trim().max(200).optional(),
});
export const aiChatCreateConversationSchema = z.object({
    title: z.string().trim().max(120).optional(),
});
export const aiChatUpdateConversationSchema = z.object({
    title: z.string().trim().min(1).max(120),
});
export const aiChatSendMessageSchema = z.object({
    text: z.string().trim().min(1).max(8000),
    /** The recipe the user is looking at, if any – the model may read it without being told the id. */
    openRecipeId: uuidSchema.nullable().optional(),
    /** The message was spoken and the answer is read aloud: the model keeps it short and plain. */
    voice: z.boolean().optional(),
});
export const aiChatSendMessageResponseSchema = z.object({
    userMessage: aiChatMessageSchema,
    assistantMessageId: uuidSchema,
    /** SSE stream of the answer (AIChatEvent), also for re-subscribing after a reload. */
    streamUrl: z.string(),
});
export const aiChatAskAnswerSchema = z.object({ allow: z.boolean() });
export const aiChatUsageSchema = z.object({
    inputTokens: z.number().int().nonnegative(),
    outputTokens: z.number().int().nonnegative(),
    cacheReadTokens: z.number().int().nonnegative(),
    cacheWriteTokens: z.number().int().nonnegative(),
});
/** Server-sent events of `/ai-chat/conversations/{id}/events` – one turn, then `done` or `error`. */
export const aiChatEventSchema = z.discriminatedUnion('type', [
    z.object({ type: z.literal('init'), sessionId: z.string() }),
    z.object({ type: z.literal('delta'), text: z.string() }),
    z.object({
        type: z.literal('tool'),
        phase: z.enum(['start', 'done', 'error']),
        name: z.string(),
        params: aiChatToolParamsSchema.default({}),
        message: z.string().nullable().optional(),
        card: aiChatCardSchema.nullable().optional(),
    }),
    z.object({
        type: z.literal('ask'),
        askId: z.string(),
        tool: z.string(),
        params: aiChatToolParamsSchema.default({}),
    }),
    z.object({ type: z.literal('answered'), askId: z.string(), allow: z.boolean() }),
    z.object({ type: z.literal('compacted'), trigger: z.enum(['manual', 'auto']) }),
    z.object({
        type: z.literal('done'),
        sessionId: z.string(),
        usage: aiChatUsageSchema,
        contextPercent: z.number().int().nullable(),
        interrupted: z.boolean().optional(),
    }),
    z.object({
        type: z.literal('error'),
        code: z.enum([
            'rate_limited',
            'credentials',
            'unavailable',
            'interrupted',
            'max_turns',
            'unknown',
        ]),
        message: z.string(),
        resetsAt: z.number().nullable().optional(),
    }),
]);
export const aiChatStatusSchema = z.object({
    /** The chat is configured on this server, allowed for this account and unlocked (AI + consent). */
    enabled: z.boolean(),
    reason: z
        .enum(['disabled', 'notConfigured', 'notAllowed', 'notEnabled', 'consentRequired'])
        .nullable(),
    model: z.string().nullable(),
    /** Which helpers the chat has on this server. */
    tools: z.object({
        webSearch: z.boolean(),
        sourceSearch: z.boolean(),
        video: z.boolean(),
    }),
    today: z.object({ tokens: z.number().int().nonnegative(), limit: z.number().int().positive() }),
});
/**
 * Applies a stream event to the blocks of the running answer. The server stores the answer the
 * same way, so what a reload shows equals what the stream built.
 */
export function applyAIChatEvent(blocks, event) {
    const next = blocks.map((b) => ({ ...b }));
    const closeText = () => {
        for (const b of next)
            if (b.type === 'text')
                b.open = false;
    };
    switch (event.type) {
        case 'delta': {
            const last = next[next.length - 1];
            if (last && last.type === 'text' && last.open !== false)
                last.text += event.text;
            else
                next.push({ type: 'text', text: event.text, open: true });
            return next;
        }
        case 'tool': {
            if (event.phase !== 'start') {
                for (let i = next.length - 1; i >= 0; i--) {
                    const b = next[i];
                    if (b && b.type === 'tool' && b.name === event.name && b.phase === 'start') {
                        b.phase = event.phase;
                        b.params = event.params;
                        b.message = event.message ?? null;
                        if (event.card)
                            b.card = event.card;
                        return next;
                    }
                }
            }
            closeText();
            next.push({
                type: 'tool',
                name: event.name,
                phase: event.phase,
                params: event.params,
                message: event.message ?? null,
                card: event.card ?? null,
            });
            return next;
        }
        case 'ask':
            closeText();
            next.push({
                type: 'ask',
                askId: event.askId,
                tool: event.tool,
                params: event.params,
                answer: null,
            });
            return next;
        case 'answered':
            for (const b of next)
                if (b.type === 'ask' && b.askId === event.askId)
                    b.answer = event.allow;
            return next;
        case 'compacted':
            closeText();
            next.push({ type: 'compacted', trigger: event.trigger });
            return next;
        default:
            return next;
    }
}
//# sourceMappingURL=ai-chat.js.map